import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
} from '@coreui/react-pro'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import SponsorService from 'src/service/SponsorService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import useSponsorQuillModules from './useSponsorQuillModules'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_LOGO_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_LOGO_SIZE = 5 * 1024 * 1024

const AddForm = ({ visible, onClose, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required').max(50, '50 Character Limit is allowed.'),
    image: Yup.mixed()
      .nullable()
      .required('Sponsor logo is required')
      .test('size', 'File size too large, max file size is 5 Mb', (value) =>
        value ? value.size <= MAX_LOGO_SIZE : true,
      )
      .test(
        'type',
        'Invalid file format selection',
        (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
      ),
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      website: '',
      facebook: '',
      twitter: '',
      instagram: '',
      about: '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('website', data.website)
      formData.append('facebook', data.facebook)
      formData.append('twitter', data.twitter)
      formData.append('instagram', data.instagram)
      formData.append('about', data.about)
      if (data.image) {
        formData.append('image', data.image)
      }

      setLoader(true)
      SponsorService.saveSponsor(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            actions.resetForm()
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 640 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Sponsor</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Create a sponsor for your directory and link its website and socials.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <h6 className="fw-bold mb-0">Required Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="name">Name *</CFormLabel>
            <CFormInput
              placeholder="Name"
              className={
                'form-control' + (formik.errors.name && formik.touched.name ? ' is-invalid' : '')
              }
              value={formik.values.name}
              onChange={formik.handleChange}
              aria-label="name"
              id="name"
            />
            {formik.errors.name && formik.touched.name && (
              <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="image">Sponsor Logo *</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_LOGO_TYPES}
              hint="JPG, PNG or GIF up to 5MB. Square image recommended."
              minHeight={120}
              file={formik.values.image}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Optional Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="website">Website</CFormLabel>
            <CFormInput
              placeholder="Website"
              value={formik.values.website}
              onChange={formik.handleChange}
              aria-label="website"
              id="website"
            />
            <small className="text-medium-emphasis">
              <i>Ex: https://www.myclubtap.com</i>
            </small>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="facebook">Facebook URL</CFormLabel>
            <CFormInput
              placeholder="Facebook URL"
              value={formik.values.facebook}
              onChange={formik.handleChange}
              aria-label="facebook"
              id="facebook"
            />
            <small className="text-medium-emphasis">
              <i>Ex: https://www.facebook.com/myclubtap</i>
            </small>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="twitter">Twitter URL</CFormLabel>
            <CFormInput
              placeholder="Twitter URL"
              value={formik.values.twitter}
              onChange={formik.handleChange}
              aria-label="twitter"
              id="twitter"
            />
            <small className="text-medium-emphasis">
              <i>Ex: https://www.twitter.com/myclubtap</i>
            </small>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="instagram">Instagram URL</CFormLabel>
            <CFormInput
              placeholder="Instagram URL"
              value={formik.values.instagram}
              onChange={formik.handleChange}
              aria-label="instagram"
              id="instagram"
            />
            <small className="text-medium-emphasis">
              <i>Ex: https://www.instagram.com/myclubtap</i>
            </small>
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="about">About</CFormLabel>
            <ReactQuill
              theme="snow"
              ref={quillRef}
              value={formik.values.about}
              onChange={(content) => formik.setFieldValue('about', content)}
              modules={modules}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Sponsor
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
